import Axios from 'axios';
import moment from 'moment-timezone';
import Qs from 'qs';
import config from 'src/config';
import {getLanguageCode} from 'src/i18n';
import {AuthToken} from 'src/modules/auth/authToken';

/** Debe coincidir con `CURRENT_DIVISION_HEADER` en `tenantMiddleware` (backend). */
export const PLANIWISE_CURRENT_DIVISION_HEADER = 'X-Planiwise-Current-Division-Id';

/** Debe coincidir con `PLANIWISE_CLIENT_HEADER` en el backend. */
export const PLANIWISE_CLIENT_HEADER = 'X-Planiwise-Client';

/** POST de creación de entidades con `divisionId` en backend (no import/batch/multi). */
const TENANT_POST_DIVISION_POLICY_SEGMENTS = new Set([
  'absence',
  'activo',
  'categoria',
  'comunicacion-interna',
  'evento',
  'firma',
  'gestor-documental',
  'incident',
  'mensaje-interno',
  'modelo-formulario',
  'modelo-tarea',
  'realtime-report',
  'notificacion',
  'pendiente',
  'perfil-empleado',
  'planificacion',
  'planificador-tarea',
  'project',
  'recordatorio',
  'registro',
  'sub-tarea',
  'tarea',
  'ubicacion',
]);

function shouldApplyDivisionPolicyOnRequest(options: {url?: string; method?: string}): boolean {
  const url = options.url || '';
  const method = (options.method || 'get').toLowerCase();
  if (method !== 'post') {
    return false;
  }
  if (/\/import|\/multi|sync-analytics|\/import-batch/i.test(url)) {
    return false;
  }
  const m = url.match(/^\/tenant\/[^/]+\/([^/?]+)/);
  if (!m) {
    return false;
  }
  return TENANT_POST_DIVISION_POLICY_SEGMENTS.has(m[1]);
}

const authAxios = Axios.create({
  baseURL: config.backendUrl,
  paramsSerializer: function (params) {
    return Qs.stringify(params, {
      arrayFormat: 'brackets',
      filter: (prefix, value) => {
        if (moment.isMoment(value) || value instanceof Date) {
          return value.toISOString();
        }

        return value;
      },
    });
  },
});

authAxios.interceptors.request.use(
  async function (options) {
    const token = AuthToken.get();

    if (token) {
      options.headers['Authorization'] = `Bearer ${token}`;
    }

    options.headers['Accept-Language'] = getLanguageCode();
    options.headers[PLANIWISE_CLIENT_HEADER] = 'web';

    // Lazy: evita cargar reducers/exporter fields antes de i18nInit (index.tsx).
    const getStore = require('src/modules/store').default;
    const divisionId = getStore()?.getState?.()?.auth?.currentDivisionId;
    if (divisionId != null && String(divisionId).trim() !== '') {
      options.headers[PLANIWISE_CURRENT_DIVISION_HEADER] = String(divisionId).trim();
    }

    // send a header to skip redirect to ngrok's browser warning page
    options.headers['ngrok-skip-browser-warning'] = true;

    if (shouldApplyDivisionPolicyOnRequest(options) && options.data && typeof options.data === 'object') {
      const body = options.data as {data?: Record<string, any>};
      if (body.data && typeof body.data === 'object' && !Array.isArray(body.data)) {
        const {applyTenantDivisionPolicyToApiPayload} =
          require('src/view/shared/division/applyTenantDivisionPolicyToApiPayload');
        applyTenantDivisionPolicyToApiPayload(body.data);
      }
    }

    return options;
  },
  function (error) {
    console.log('Request error: ', error);
    return Promise.reject(error);
  },
);

export default authAxios;
